const yearKey = (year) => (year === null || year === undefined ? '' : String(year));

// FO-CRD- and RIA-CRD- spell one firm, as People::Affinities::CompanyLookup#spellings does.
const firmKey = (id) => String(id).replace(/^RIA-CRD-/, 'FO-CRD-');

export const spellKey = (row, idField) => [firmKey(row[idField]), yearKey(row.start_year), yearKey(row.end_year)].join('|');

const yearNumber = (year) => (year === null || year === undefined || year === '' ? null : Number(year));

// Inclusive, with a missing year read as open, as People::Affinities::SpellHistory.overlap? does.
const overlaps = (one, other) => {
    const [start, end] = [yearNumber(one.start_year), yearNumber(one.end_year)];
    const [otherStart, otherEnd] = [yearNumber(other.start_year), yearNumber(other.end_year)];

    return (start ?? -Infinity) <= (otherEnd ?? Infinity) && (otherStart ?? -Infinity) <= (end ?? Infinity);
};

// The earliest known start and the latest end; an open end stays open ("Present").
const widened = (row, other) => {
    const starts = [row.start_year, other.start_year].map(yearNumber).filter((year) => year !== null);
    const ends = [row.end_year, other.end_year].map(yearNumber);

    return {
        ...row,
        start_year: starts.length ? Math.min(...starts) : null,
        end_year: ends.includes(null) ? null : Math.max(...ends),
    };
};

// An accepted match adds its rows to the contact's instead of replacing them. Rows the save clears
// on adoption lose their id so it recreates them; rows that stay on the same record (keepIds) keep
// it. Of two duplicates the persisted one wins, and a second persisted one is
// destroyed rather than left behind unposted. With widenOverlaps, an imported spell whose years
// overlap one already listed for the same firm stretches that row to cover both instead of adding
// a second one.
export const mergeImportedRows = (currentRows, importedRows, idField, {keepIds = false, widenOverlaps = false} = {}) => {
    const current = (currentRows || [])
        .filter((row) => keepIds || !row._destroy)
        .map((row) => (keepIds ? row : {...row, id: null}));
    const merged = [];
    const indexByKey = new Map();

    [...current, ...(importedRows || [])].forEach((row, index) => {
        if (row._destroy || !row[idField]) {
            merged.push(row);
            return;
        }

        if (widenOverlaps && index >= current.length) {
            const target = merged.findIndex((listed) => (
                !listed._destroy && listed[idField] && firmKey(listed[idField]) === firmKey(row[idField]) && overlaps(listed, row)
            ));
            if (target !== -1) {
                merged[target] = widened(merged[target], row);
                return;
            }
        }

        const key = spellKey(row, idField);
        if (!indexByKey.has(key)) {
            indexByKey.set(key, merged.length);
            merged.push(row);
        } else if (row.id && !merged[indexByKey.get(key)].id) {
            merged[indexByKey.get(key)] = row;
        } else if (row.id) {
            merged.push({...row, _destroy: true});
        }
    });

    return merged;
};

export const mergeById = (currentItems, importedItems) => {
    const seen = new Set();

    return [...(currentItems || []), ...(importedItems || [])].filter((item) => {
        const key = String(item.id);
        if (seen.has(key)) return false;

        seen.add(key);
        return true;
    });
};
